import { CountryCode, Source, SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetDataType
} from "@/types/dataset";
import { LayerType } from "@/types/map";
import { EOutputAnalysisType } from "@/types/spatial";

export const CATCHMENT_GRID_ID_COLUMN_NAME = [
  "catchment_geometry",
  "grid_geometry",
  "location_geometry"
];

export const PROFILING_LAYER_TYPES = ["polygon", "point"];

export const SPATIAL_ANALYSIS_RESULT_DATASET_NAME = "spatial analysis result";

/** Default fill/outline for the "Simple" Color Scale (shared by the config
 *  panel default and the value seeded on a new POI dataset). */
export const DEFAULT_SIMPLE_FILL_COLOR = "#088C7A";
export const DEFAULT_SIMPLE_OUTLINE_COLOR = "#127E65";

const DONUT_CHART_LIMITED_DATA = 10;
const BAR_CHART_LIMITED_DATA = 10;
const TABLE_LIMITED_DATA = 10;

export const LIMITED_DATA_SUMMARY = {
  BAR_CHART: BAR_CHART_LIMITED_DATA,
  DONUT_CHART: DONUT_CHART_LIMITED_DATA,
  TABLE: TABLE_LIMITED_DATA
};

export const SPATIAL_TYPE = [
  EOutputAnalysisType.GRID,
  EOutputAnalysisType.PROFILING
];

export const SPATIAL_AGGREGATION_TYPE = {
  All: "all",
  Point: "point",
  Polygon: "polygon",
  AdminArea: "admin"
};

export const COUNTRY_NAME: Record<CountryCode | "all", string> = {
  all: "All",
  [""]: "Global", // CountryCode "Global" could sometimes be represented by an empty string for its value, but they are equivalent
  global: "Global",
  id: "Indonesia",
  vn: "Vietnam",
  my: "Malaysia",
  jp: "Japan"
};

export const COUNTRY_DATA: { label: string; value: CountryCode }[] = [
  { label: "Global", value: "" }, // Global is represented by an empty string value in BE to skip filtering by a specific country
  { label: "Indonesia", value: "id" },
  { label: "Vietnam", value: "vn" },
  { label: "Malaysia", value: "my" },
  { label: "Japan", value: "jp" }
];

export const SPATIAL_AGGREGATION_NAME: Record<
  "all" | "poi" | "polygon" | "adm_area" | "H3" | "geohash" | "line" | "none",
  string
> = {
  all: "All",
  poi: "Point",
  polygon: "Polygon",
  adm_area: "Adm. Area",
  H3: "H3",
  geohash: "Geohash",
  line: "Line",
  none: "None"
};

export const SOURCE_NAME: Record<Source | "", string> = {
  bvt: "Bvarta Data",
  user: "My Organization",
  "": ""
};

export const SPATIAL_TYPE_TO_LAYER_TYPE: Record<
  SpatialAggregationType,
  LayerType
> = {
  poi: LayerType.POINT,
  polygon: LayerType.POLYGON,
  adm_area: LayerType.ADMINISTRATIVE,
  H3: LayerType.H3,
  geohash: LayerType.GEOHASH,
  [EOutputAnalysisType.GRID]: LayerType.GRID,
  [EOutputAnalysisType.PROFILING]: LayerType.PROFILING,
  line: LayerType.LINE
};

export const DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE: DatasetAttributeSchema = {
  id: "",
  code: "",
  attribute_name: "",
  data_type: DataSchemaType.INT,
  is_nullable: true,
  is_spatial: false,
  position: 0,
  created_at: "",
  updated_at: "",
  is_reference_geo: false,
  is_reference_id: false,
  is_visible: true,
  paired_key_attribute_id: null
};

export const ORDINAL_TYPES: Omit<
  DataSchemaType,
  | DataSchemaType.INT
  | DataSchemaType.FLOAT
  | DataSchemaType.PERCENTAGE
  | DataSchemaType.RATIO
  | DataSchemaType.LATITUDE
  | DataSchemaType.LONGITUDE
>[] = [
  DataSchemaType.ADDRESS,
  DataSchemaType.TITLE,
  DataSchemaType.TEXT,
  DataSchemaType.TEXT2,
  DataSchemaType.BOOL,
  DataSchemaType.JSON,
  DataSchemaType.GEOMETRY,
  DataSchemaType.DATE,
  DataSchemaType.TIME,
  DataSchemaType.DATE_TIME,
  DataSchemaType.ID
];

export const ORDINAL_TYPES_COLUMN: Omit<
  DatasetDataType,
  | DatasetDataType.TYPE_INTEGER
  | DatasetDataType.TYPE_FLOAT
  | DatasetDataType.TYPE_PERCENTAGE
  | DatasetDataType.TYPE_RATIO
  | DatasetDataType.TYPE_LATITUDE
  | DatasetDataType.TYPE_LONGITUDE
>[] = [
  DatasetDataType.TYPE_ADDRESS,
  DatasetDataType.TYPE_TITLE,
  DatasetDataType.TYPE_STRING,
  DatasetDataType.TYPE_BOOLEAN,
  DatasetDataType.TYPE_JSON,
  DatasetDataType.TYPE_DATETIME,
  DatasetDataType.TYPE_DATE,
  DatasetDataType.TYPE_ID
];

export const DATA_TYPE_MAPPINGS = {
  [DatasetDataType.TYPE_STRING]: "string_value",
  [DatasetDataType.TYPE_INTEGER]: "int_value",
  [DatasetDataType.TYPE_FLOAT]: "float_value",
  [DatasetDataType.TYPE_BOOLEAN]: "bool_value",
  [DatasetDataType.TYPE_DATETIME]: "string_value",
  [DatasetDataType.TYPE_JSON]: "json_value",
  [DatasetDataType.TYPE_GEOMETRY]: "geometry_value",
  [DatasetDataType.TYPE_DATE]: "string_value",
  [DatasetDataType.TYPE_TIME]: "string_value",
  [DatasetDataType.TYPE_ADDRESS]: "string_value",
  [DatasetDataType.TYPE_TITLE]: "string_value",
  [DatasetDataType.TYPE_PHOTOS]: "json_value",
  [DatasetDataType.TYPE_LINKS]: "json_value"
};

export const DATA_TYPE_VALUES = {
  [DatasetDataType.TYPE_STRING]: DataSchemaType.TEXT,
  [DatasetDataType.TYPE_INTEGER]: DataSchemaType.INT,
  [DatasetDataType.TYPE_FLOAT]: DataSchemaType.FLOAT,
  [DatasetDataType.TYPE_BOOLEAN]: DataSchemaType.BOOL,
  [DatasetDataType.TYPE_DATETIME]: DataSchemaType.DATE_TIME,
  [DatasetDataType.TYPE_JSON]: DataSchemaType.JSON,
  [DatasetDataType.TYPE_GEOMETRY]: DataSchemaType.GEOMETRY,
  [DatasetDataType.TYPE_DATE]: DataSchemaType.DATE,
  [DatasetDataType.TYPE_TIME]: DataSchemaType.TIME,
  [DatasetDataType.TYPE_ADDRESS]: DataSchemaType.ADDRESS,
  [DatasetDataType.TYPE_TITLE]: DataSchemaType.TITLE,
  [DatasetDataType.TYPE_PHOTOS]: DataSchemaType.JSON,
  [DatasetDataType.TYPE_LINKS]: DataSchemaType.JSON,
  [DatasetDataType.TYPE_LATITUDE]: DataSchemaType.LATITUDE,
  [DatasetDataType.TYPE_LONGITUDE]: DataSchemaType.LONGITUDE,
  [DatasetDataType.TYPE_PERCENTAGE]: DataSchemaType.PERCENTAGE,
  [DatasetDataType.TYPE_RATIO]: DataSchemaType.RATIO,
  [DatasetDataType.TYPE_ID]: DataSchemaType.ID
};

export const MAX_POI: number = Number(import.meta.env.VITE_MAX_POI);
export const MIN_STEP = 2;
export const MAX_STEP = 10;
export const DEFAULT_STEP = 10;
export const MAX_RANKING_INPUT = 100;
export const MIN_STROKE_WIDTH = 1;
export const MAX_STROKE_WIDTH = 5;
export const OTHER_ROW_ID = "__other__";

// Legend show/hide key for the Custom Ordinal "Other" bucket, kept distinct from
// the Simple/value-based "others" sentinel so their hide state never collides.
export const CUSTOM_ORDINAL_OTHER_LEGEND_KEY = "custom-ordinal-other";

export const ROW_ID_DETAIL_DATASET = "orbis_li_id";

/** Maximum total input rows allowed across all datasets selected for a spatial analysis. */
export const SPATIAL_ANALYSIS_INPUT_ROWS_TOTAL_LIMIT: number = Number(
  import.meta.env.VITE_SPATIAL_ANALYSIS_INPUT_ROWS_TOTAL_LIMIT
);

export const DEFAULT_LIMIT_THRESHOLD =
  Number.isFinite(SPATIAL_ANALYSIS_INPUT_ROWS_TOTAL_LIMIT) &&
  SPATIAL_ANALYSIS_INPUT_ROWS_TOTAL_LIMIT > 0
    ? SPATIAL_ANALYSIS_INPUT_ROWS_TOTAL_LIMIT
    : 350_000;

/**
 * Per the PRD, the hard block must trigger when data volume is within ±5% of
 * the limit ("at or near the limit"), not only once it strictly exceeds it.
 * So the effective block threshold is 95% of the configured limit.
 */

export const LIMIT_THRESHOLD_BLOCK = DEFAULT_LIMIT_THRESHOLD * 0.95;
